import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/auth/AuthContext";
import { useAuth } from "@/auth/AuthContext";
import { useT } from "@/i18n/I18nContext";
import { PageHeader, Stat, Card, SignalBadge, Grade, Button } from "@/components/ui-kit";
import { ChartLineUp, ArrowRight, TrendUp, TrendDown, X, Fire, Medal } from "@phosphor-icons/react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { MonthlyGoalCard } from "@/components/MonthlyGoalCard";
import { BadgeCelebration } from "@/components/BadgeCelebration";
import { InstallAppButton } from "@/components/InstallAppButton";

const Dashboard = () => {
  const { t, lang } = useT();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [community, setCommunity] = useState(null);
  const [selected, setSelected] = useState(null);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, a, c, st] = await Promise.all([
          api.get("/journal/stats").then(r => r.data),
          api.get("/analyses").then(r => r.data),
          api.get("/community/stats").then(r => r.data).catch(() => null),
          api.get("/journal/streak").then(r => r.data).catch(() => null),
        ]);
        setStats(s);
        setAnalyses(a);
        setCommunity(c);
        setStreak(st);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <PageHeader
        title={`${t("dash.subtitle")}, ${user?.full_name?.split(" ")[0] || ""}`}
        sub={lang === "cs" ? "Tvoje obchodní centrála" : "Your trading command center"}
        action={
          <div className="flex items-center gap-3 flex-wrap">
            <InstallAppButton lang={lang} />
            {streak && (
              <div
                data-testid="streak-badge"
                title={lang === "cs" ? `Nejdelší série: ${streak.best} dní` : `Best streak: ${streak.best} days`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-mono border ${streak.current > 0 ? "border-orange-500/40 bg-orange-500/10 text-orange-400" : "border-zinc-800 bg-zinc-900/50 text-zinc-500"}`}
              >
                <Fire weight={streak.current > 0 ? "fill" : "bold"} size={16} />
                {streak.current > 0
                  ? (lang === "cs" ? `${streak.current} ${streak.current === 1 ? "den" : streak.current < 5 ? "dny" : "dní"} v řadě` : `${streak.current}-day streak`)
                  : (lang === "cs" ? "Zapiš dnešní obchod a začni sérii" : "Log a trade to start a streak")}
                {streak.current > 0 && !streak.journaled_today && (
                  <span className="text-[10px] text-zinc-500 ml-1">{lang === "cs" ? "· dnes ještě nezapsáno!" : "· not logged today!"}</span>
                )}
              </div>
            )}
            {streak?.badges?.some((b) => b.earned) && (
              <div className="flex items-center gap-1.5" data-testid="streak-badges">
                {streak.badges.filter((b) => b.earned).map((b) => (
                  <span
                    key={b.days}
                    data-testid={`streak-badge-${b.days}`}
                    title={lang === "cs" ? `Odznak: ${b.days} dní deníku v řadě` : `Badge: ${b.days}-day journaling streak`}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-mono border border-amber-500/40 bg-amber-500/10 text-amber-400"
                  >
                    <Medal weight="fill" size={13} /> {b.days}
                  </span>
                ))}
              </div>
            )}
            <Link to="/app/analyze" data-testid="dash-new-analysis" className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md text-sm transition-colors flex items-center gap-2">
              <ChartLineUp weight="bold" size={16} /> {t("dash.startAnalysis")}
            </Link>
          </div>
        }
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dash-stats">
          <Stat label={t("dash.plan")} value={user?.plan || "FREE"} testid="stat-plan" />
          <Stat label={t("dash.winrate")} value={loading ? "..." : `${stats?.win_rate || 0}%`} testid="stat-winrate" />
          <Stat label="Profit Factor" value={loading ? "..." : (stats?.profit_factor || 0)} testid="stat-pf" />
          <Stat label={t("dash.totalpnl")} value={loading ? "..." : `$${(stats?.total_pnl || 0).toFixed(2)}`} accent={(stats?.total_pnl || 0) >= 0 ? "green" : "red"} testid="stat-pnl" />
        </div>

        <MonthlyGoalCard stats={stats} lang={lang} />

        {community && (
          <Card className="p-6" data-testid="community-stats">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-lg font-medium text-zinc-100">{lang === "cs" ? "Statistiky komunity" : "Community stats"}</h3>
              <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">{community.analyses_7d} {lang === "cs" ? "analýz / 7 dní" : "analyses / 7d"}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="flex justify-between text-xs font-mono uppercase tracking-wider mb-2">
                  <span className="text-green-400">Bull {community.sentiment.bull_pct}%</span>
                  <span className="text-red-400">Bear {community.sentiment.bear_pct}%</span>
                </div>
                <div className="h-2.5 bg-zinc-900 rounded-full overflow-hidden flex" data-testid="community-sentiment-bar">
                  <div className="h-full bg-green-500" style={{ width: `${community.sentiment.bull_pct}%` }} />
                  <div className="h-full bg-red-500" style={{ width: `${community.sentiment.bear_pct}%` }} />
                </div>
                <div className="text-xs text-zinc-500 mt-2 font-mono">{lang === "cs" ? "Sentiment z AI analýz (BUY vs SELL)" : "Sentiment from AI analyses (BUY vs SELL)"}</div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">{lang === "cs" ? "Nejanalyzovanější aktiva" : "Most analyzed assets"}</div>
                <div className="flex flex-wrap gap-1.5">
                  {community.top_instruments.length === 0 && <span className="text-xs text-zinc-600">—</span>}
                  {community.top_instruments.map((ti) => (
                    <span key={ti.instrument} className="text-xs font-mono px-2 py-1 rounded-md bg-zinc-900 border border-zinc-800 text-zinc-300">
                      {ti.instrument} <span className="text-zinc-500">×{ti.count}</span>
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex gap-6">
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{lang === "cs" ? "Win rate komunity" : "Community win rate"}</div>
                  <div className="text-2xl font-mono font-medium text-zinc-100" data-testid="community-winrate">{community.community_win_rate}%</div>
                  <div className="text-xs text-zinc-600 font-mono">{community.closed_trades_total} {lang === "cs" ? "obchodů" : "trades"}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{lang === "cs" ? "AI známky" : "AI grades"}</div>
                  <div className="flex flex-wrap gap-1">
                    {["A+", "A", "B", "C", "D", "F"].filter(g => community.grade_distribution[g]).map(g => (
                      <span key={g} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800 text-zinc-300">{g}:{community.grade_distribution[g]}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-zinc-100">{t("dash.equity")}</h3>
              <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">{stats?.closed_trades || 0} trades</span>
            </div>
            {stats?.equity_curve?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={stats.equity_curve}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#71717a" }} tickFormatter={(d) => new Date(d).toLocaleDateString()} />
                    <YAxis tick={{ fontSize: 10, fill: "#71717a" }} />
                    <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #27272a", borderRadius: 6, fontSize: 12 }} />
                    <Line type="monotone" dataKey="equity" stroke="#e3b341" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-zinc-500 text-sm">
                {lang === "cs" ? "Po prvních uzavřených obchodech se zde zobrazí equity křivka." : "Equity curve will appear after your first closed trades."}
              </div>
            )}
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-medium text-zinc-100 mb-4">{lang === "cs" ? "Statistiky" : "Stats"}</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-baseline">
                <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Wins</span>
                <span className="text-xl font-mono text-green-400">{stats?.wins || 0}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Losses</span>
                <span className="text-xl font-mono text-red-400">{stats?.losses || 0}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">{lang === "cs" ? "Otevřené" : "Open"}</span>
                <span className="text-xl font-mono text-zinc-100">{stats?.open_trades || 0}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Avg RR</span>
                <span className="text-xl font-mono text-zinc-100">{stats?.avg_rr || 0}</span>
              </div>
            </div>
          </Card>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-zinc-100">{t("dash.recent")}</h3>
            <Link to="/app/analyze" className="text-xs font-mono uppercase tracking-wider text-blue-400 hover:text-blue-300 flex items-center gap-1">
              {lang === "cs" ? "Nová" : "New"} <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
          {analyses.length === 0 ? (
            <div className="text-center py-12 text-zinc-500" data-testid="dash-empty-analyses">
              <ChartLineUp size={40} weight="thin" className="mx-auto mb-3 text-zinc-700" />
              <p className="text-sm">{t("dash.empty.analyses")}</p>
              <Link to="/app/analyze" className="inline-block mt-4 text-blue-400 hover:text-blue-300 text-sm">{t("dash.startAnalysis")} →</Link>
            </div>
          ) : (
            <div className="space-y-2">
              {analyses.slice(0, 6).map((a) => {
                const r = a.result || {};
                return (
                  <div
                    key={a.id}
                    onClick={() => setSelected(a)}
                    data-testid={`dash-analysis-row-${a.id}`}
                    className="flex items-center gap-4 p-3 rounded-md hover:bg-zinc-900 transition-colors border border-transparent hover:border-zinc-800 cursor-pointer"
                  >
                    <Grade grade={r.setup_grade || "C"} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-medium text-zinc-100">{r.instrument || "—"}</span>
                        <span className="text-xs font-mono text-zinc-500">{r.timeframe || ""}</span>
                        <SignalBadge direction={r.direction || "WAIT"} />
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 line-clamp-1">{r.reasoning || "—"}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">Confidence</div>
                      <div className="text-sm font-mono text-zinc-100">{r.confidence || 0}%</div>
                    </div>
                    <div className="text-right hidden sm:block">
                      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">RR</div>
                      <div className="text-sm font-mono text-zinc-100">{r.risk_reward || "—"}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
      {selected && <AnalysisDetailModal analysis={selected} onClose={() => setSelected(null)} lang={lang} />}
      <BadgeCelebration streak={streak} lang={lang} />
    </div>
  );
};

const DetailRow = ({ label, value, color = "text-zinc-100" }) => (
  <div className="flex justify-between items-baseline py-1.5 border-b border-zinc-900">
    <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</span>
    <span className={`font-mono text-sm font-medium ${color}`}>{value ?? "—"}</span>
  </div>
);

const AnalysisDetailModal = ({ analysis, onClose, lang }) => {
  const r = analysis.result || {};
  const cs = lang === "cs";
  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="analysis-detail-modal">
        <div className="flex items-center justify-between border-b border-zinc-800 px-6 py-4 sticky top-0 bg-zinc-950/95 backdrop-blur">
          <div className="flex items-center gap-3">
            <Grade grade={r.setup_grade || "C"} />
            <div>
              <div className="font-mono font-medium text-zinc-100">{r.instrument || "—"} <span className="text-zinc-500 text-xs">{r.timeframe || ""}</span></div>
              <div className="text-xs text-zinc-500">{(analysis.created_at || "").substring(0, 16).replace("T", " ")}</div>
            </div>
            <SignalBadge direction={r.direction || "WAIT"} />
          </div>
          <button onClick={onClose} data-testid="analysis-detail-close" className="text-zinc-400 hover:text-zinc-100 transition-colors p-1"><X weight="bold" size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-x-6">
            <DetailRow label={cs ? "Důvěra" : "Confidence"} value={`${r.confidence || 0}%`} />
            <DetailRow label="R:R" value={r.risk_reward ? `1:${r.risk_reward}` : "—"} />
            <DetailRow label="Entry" value={r.entry} />
            <DetailRow label="Stop Loss" value={r.stop_loss} color="text-red-400" />
            <DetailRow label="TP 1" value={r.take_profit_1} color="text-green-400" />
            <DetailRow label="TP 2" value={r.take_profit_2} color="text-green-400" />
            <DetailRow label="TP 3" value={r.take_profit_3} color="text-green-400" />
            <DetailRow label={cs ? "Úspěšnost" : "Success prob."} value={r.success_probability ? `${r.success_probability}%` : "—"} />
          </div>
          {r.reasoning && (
            <div>
              <h4 className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5">{cs ? "Zdůvodnění" : "Reasoning"}</h4>
              <p className="text-sm text-zinc-300 leading-relaxed" data-testid="analysis-detail-reasoning">{r.reasoning}</p>
            </div>
          )}
          {r.invalidation && (
            <div>
              <h4 className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5">{cs ? "Invalidace" : "Invalidation"}</h4>
              <p className="text-sm text-zinc-300 leading-relaxed">{r.invalidation}</p>
            </div>
          )}
          {Array.isArray(r.risks) && r.risks.length > 0 && (
            <div>
              <h4 className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5">{cs ? "Rizika" : "Risks"}</h4>
              <ul className="text-sm text-zinc-300 space-y-1 list-disc list-inside">
                {r.risks.map((risk, i) => <li key={i}>{risk}</li>)}
              </ul>
            </div>
          )}
          {r.position_management && (
            <div>
              <h4 className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5">{cs ? "Řízení pozice" : "Position management"}</h4>
              <p className="text-sm text-zinc-300 leading-relaxed">{r.position_management}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
